import * as z from 'zod';
import { alertKindSchema, alertSeveritySchema } from './alert.js';
import { isoDateTimeSchema, strict, uuidSchema } from './common.js';
import { directionSchema, occupancyFrameSchema, presenceReasonCodeSchema } from './presence.js';
/**
 * AD-57's event stream, as a wire contract rather than as four interfaces on the server.
 *
 * **`./common.js` is imported first, and the order is load-bearing (R391).** zod probes
 * `new Function('')` when the FIRST `z.object()` is constructed and caches the answer, and
 * `common.ts` is what imports `zod-runtime.js` to set `jitless` before that happens. Every schema
 * module in this package imports `common.js`, which is the one ordering no bundler can rearrange;
 * a module that reached for `z.object` before it would make the browser's Content-Security-Policy
 * report a violation on the first page load.
 *
 * **Only one of these four shapes had a schema before, and that asymmetry was the problem.**
 * `occupancyFrameSchema` lived in `presence.ts` and was documented as a wire contract; the anomaly,
 * the record-changed notice and the alert were TypeScript interfaces inside
 * `packages/server/src/realtime/events.ts`, visible to nothing that consumes them. A client cannot
 * parse an interface, and `INTEGRATION.md` documents all four — so the three that were server-local
 * were three shapes with no single declaration, which is the condition every drift in this
 * repository has started from. The server now derives its payload types from these.
 *
 * **A payload carries ids, counts and closed-set tokens, and never a name.** The stream is fanned
 * out to every connected terminal with no filtering of any kind, so a payload carrying a person's
 * name would put personal data on every screen in the building for an event most of them have no
 * business with. A client that needs the person reads them through a separate, separately
 * authorised and separately audited request.
 */
/** The live headcount. Two counts and an instant; `overdue` deliberately comes from the route. */
export const occupancyEventSchema = strict({
    type: z.literal('occupancy'),
    data: occupancyFrameSchema,
});
/**
 * One anomaly: a movement whose result was an override.
 *
 * The reason code is a closed token the client renders in its own language. The NOTE that may
 * accompany it is never here — a note is prose an operator wrote about a specific human, and it is
 * sealed under that person's key.
 */
export const anomalyEventSchema = strict({
    type: z.literal('anomaly'),
    data: strict({
        eventId: uuidSchema,
        personId: uuidSchema,
        direction: directionSchema,
        result: z.enum(['blocked_override', 'override']),
        reasonCode: presenceReasonCodeSchema.nullable(),
        occurredAt: isoDateTimeSchema,
    }),
});
/** A record changed, so an open list or detail view asks for it again. */
export const personChangedEventSchema = strict({
    type: z.literal('person-changed'),
    data: strict({
        personId: uuidSchema,
        op: z.enum(['remove', 'upsert']),
    }),
});
/** An alert was raised or its severity changed. The open set is re-read over REST. */
export const alertEventSchema = strict({
    type: z.literal('alert'),
    data: strict({
        alertId: uuidSchema,
        kind: alertKindSchema,
        severity: alertSeveritySchema,
        raisedAt: isoDateTimeSchema,
    }),
});
/**
 * One frame, as a discriminated union over its `type`.
 *
 * Discriminated rather than a plain union so a parse failure names the member it tried, and so a
 * consumer narrowing on `type` gets the payload from the union instead of being able to build one
 * without its tag.
 */
export const realtimeEventSchema = z.discriminatedUnion('type', [
    occupancyEventSchema,
    anomalyEventSchema,
    personChangedEventSchema,
    alertEventSchema,
]);
/**
 * Every event type the stream carries. Nothing else may be sent.
 *
 * Derived from the union rather than written again beside it: a fifth member added to the union and
 * forgotten here would be a list that silently stopped describing the stream, and this list is what
 * the server's own "no `id:` line, ever" test iterates.
 */
export const REALTIME_EVENT_TYPES = [
    'occupancy',
    'anomaly',
    'person-changed',
    'alert',
];
//# sourceMappingURL=realtime.js.map